"use client"

import { useState } from "react"

import { Portal, Transition } from "@headlessui/react"
import Image from "next/image"
import { useTranslations } from "next-intl"

import { CATEGORY_PLACEMENTS } from "@/components/marketplace/category-placements"
import { categoryVisual } from "@/components/marketplace/category-visual"
import { APP_STORE_LINKS } from "@/components/marketplace/layout/footer-links"
import { NavLanguageToggle } from "@/components/marketplace/layout/nav-language-toggle"
import { NAV_EYEBROW, NAV_ITEM, NAV_LABEL, NAV_ROW, shortLabel } from "@/components/marketplace/layout/nav-type"
import { StoreBadges } from "@/components/marketplace/layout/store-badges"
import { Wordmark } from "@/components/marketplace/layout/wordmark"
import { withPalette } from "@/components/marketplace/palette"
import type { NavCategory } from "@/components/marketplace/types"
import { Button, ChevronIcon, CloseIcon, CountBadge, Grid, Heading, IconButton, PlusIcon, Row, Scrim, SearchInput, Stack, Text } from "@/components/ui"
import { ExternalLink } from "@/components/ui/external-link"
import { LocalizedClientLink } from "@/components/ui/localized-client-link"
import { HAS_LANGUAGE_CHOICE } from "@/i18n/locales"
import { useLocalizedPush } from "@/lib/hooks/use-navigation"
import { useOverlayDismiss, useOverlayPreview } from "@/lib/hooks/use-overlay-dismiss"
import { buildRoute, ROUTES } from "@/lib/routes"
import { retrieveUTMParams } from "@/lib/services/utm"
import { useClientSessionId } from "@/platform/session/auth/use-client-session-id"
import { useWishlistIds } from "@/platform/shopper/use-wishlist"

interface HelpItem {
  label: string
  href: string
  external?: boolean
}

const FAQ_URL = "https://www.nelo.mx/es/preguntas-frecuentes"

type DrawerTranslator = ReturnType<typeof useTranslations<"navDrawer">>

const getHelpItems = (t: DrawerTranslator): HelpItem[] => [
  { label: t("howItWorks"), href: `/${ROUTES.HOW_TO_BUY}` },
  { label: t("helpCenter"), href: FAQ_URL, external: true },
  { label: t("sellOnNelo"), href: `/${ROUTES.FOR_MERCHANTS}` },
]

const FEATURED_COUNT = CATEGORY_PLACEMENTS.menuFeatured

const CHILD_ROW = `text-fg-muted hover:bg-surface-subtle hover:text-fg h-auto min-h-11 justify-start rounded-none py-250 pr-400 pl-1200 ${NAV_ITEM}`

// The list is sixteen rows of the same shape, so it needs something to land on first.
// The four leading departments come in as photographs — the same lever the feed uses,
// and the only thing in the drawer with any weight.
const FeaturedTile = ({ category, onClose }: { category: NavCategory; onClose?: () => void }) => {
  const { image } = categoryVisual(category.handle, category.photo)
  return (
    <LocalizedClientLink href={buildRoute.category(category.path)} onClick={onClose} className="press rounded-12x aspect-tile relative block overflow-hidden">
      <Image src={image} alt="" fill sizes="45vw" className="object-cover" />
      <span className={`bg-surface ${NAV_LABEL} text-fg-strong absolute bottom-200 left-200 line-clamp-1 rounded-full px-300 py-100`}>{shortLabel(category.label)}</span>
    </LocalizedClientLink>
  )
}

export const NavDrawer = ({ open, onClose, categories, bagCount = 0 }: { open: boolean; onClose?: () => void; categories: NavCategory[]; bagCount?: number }) => {
  const isPreview = useOverlayPreview()
  useOverlayDismiss(open, onClose)
  const { sessionId, isMounted } = useClientSessionId()
  const signupHref = buildRoute.externalSignup(sessionId, isMounted ? retrieveUTMParams() : undefined)
  const t = useTranslations("navDrawer")
  const tNav = useTranslations("nav")
  const helpItems = getHelpItems(t)
  const featured = categories.slice(0, FEATURED_COUNT)
  const { savedIds } = useWishlistIds()
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [searchText, setSearchText] = useState("")
  const localizedPush = useLocalizedPush()
  const goToResults = () => {
    onClose?.()
    localizedPush(buildRoute.search({ text: searchText.trim() }))
  }
  const toggle = (handle: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(handle)) next.delete(handle)
      else next.add(handle)
      return next
    })

  return (
    <Portal enabled={!isPreview}>
      {open && <Scrim label={t("close")} onDismiss={() => onClose?.()} className="z-modal inset-0 sm:block" />}
      <Transition
        appear
        show={open}
        as="div"
        role="dialog"
        aria-modal="true"
        aria-label={t("dialogLabel")}
        className="bg-surface z-modal shadow-modal fixed inset-y-0 left-0 flex max-h-dvh w-full flex-col sm:max-w-sm"
        enter="ease-expressive duration-slow transition motion-reduce:transition-none"
        enterFrom="-translate-x-full"
        enterTo="translate-x-0"
        leave="ease-productive duration-moderate transition motion-reduce:transition-none"
        leaveFrom="translate-x-0"
        leaveTo="-translate-x-full"
      >
        <Row justify="between" className="bg-surface border-line-subtle h-15 border-b px-400">
          <Wordmark tone="brand" height={22} />
          <IconButton aria-label={t("close")} variant="ghost" className="text-fg hover:bg-surface-subtle size-9" icon={<CloseIcon />} onClick={onClose} />
        </Row>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className="border-line-subtle border-b px-400 py-350">
            <SearchInput
              tone="line"
              submit={false}
              placeholder={t("searchPlaceholder")}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") goToResults()
              }}
            />
          </div>
          {featured.length > 0 && (
            <>
              <div className={`${NAV_EYEBROW} pt-400`}>{t("featuredEyebrow")}</div>
              <Grid cols={2} gap="250" className="px-400 pt-250 pb-200">
                {featured.map((category) => (
                  <FeaturedTile key={category.handle} category={category} onClose={onClose} />
                ))}
              </Grid>
            </>
          )}
          <div className="py-150">
            <div className={`${NAV_EYEBROW} pb-150`}>{t("shopEyebrow")}</div>
            <Button variant="quiet" fullWidth asChild onClick={onClose} className={NAV_ROW}>
              <LocalizedClientLink href={buildRoute.category()}>
                <span className="flex-1 text-left">{t("allCategories")}</span>
                <ChevronIcon size={14} className="text-fg-subtle opacity-50" />
              </LocalizedClientLink>
            </Button>
            {categories.map((category) => {
              const children = category.children ?? []
              const { palette } = categoryVisual(category.handle)
              if (children.length === 0) {
                return (
                  <Button key={category.handle} variant="quiet" fullWidth asChild onClick={onClose} className={withPalette(palette, NAV_ROW)}>
                    <LocalizedClientLink href={buildRoute.category(category.path)}>
                      <span className="flex-1 text-left">{category.label}</span>
                      <ChevronIcon size={14} className="text-fg-subtle opacity-50" />
                    </LocalizedClientLink>
                  </Button>
                )
              }
              const isOpen = expanded.has(category.handle)
              return (
                <div key={category.handle}>
                  <Button variant="quiet" fullWidth onClick={() => toggle(category.handle)} aria-expanded={isOpen} className={withPalette(palette, NAV_ROW)}>
                    <span className="flex-1 text-left">{category.label}</span>
                    <PlusIcon size={16} className={`text-fg-subtle duration-quick transition-transform ${isOpen ? "rotate-45" : ""}`} />
                  </Button>
                  {isOpen && (
                    <Stack gap="0" className="pb-150">
                      {children.map((child) => (
                        <Button key={child.handle} variant="quiet" fullWidth asChild onClick={onClose} className={CHILD_ROW}>
                          <LocalizedClientLink href={buildRoute.category(child.path)}>{child.label}</LocalizedClientLink>
                        </Button>
                      ))}
                      <Button variant="quiet" fullWidth asChild onClick={onClose} className={`${CHILD_ROW} text-fg-brand font-medium`}>
                        <LocalizedClientLink href={buildRoute.category(category.path)}>{tNav("seeAllCategory", { category: category.label })}</LocalizedClientLink>
                      </Button>
                    </Stack>
                  )}
                </div>
              )
            })}
          </div>

          <div className="border-line-subtle mt-150 border-t py-150 pt-350">
            <div className={`${NAV_EYEBROW} pb-150`}>{t("mineEyebrow")}</div>
            <Button variant="quiet" fullWidth asChild onClick={onClose} className={NAV_ROW}>
              <LocalizedClientLink href={buildRoute.saved()}>
                <span className="flex-1 text-left">{tNav("saved")}</span>
                <CountBadge count={savedIds.size} />
                <ChevronIcon size={14} className="text-fg-subtle opacity-50" />
              </LocalizedClientLink>
            </Button>
            <Button variant="quiet" fullWidth asChild onClick={onClose} className={NAV_ROW}>
              <LocalizedClientLink href={`/${ROUTES.CART}`}>
                <span className="flex-1 text-left">{tNav("bag")}</span>
                <CountBadge count={bagCount} />
                <ChevronIcon size={14} className="text-fg-subtle opacity-50" />
              </LocalizedClientLink>
            </Button>
          </div>

          <div className="border-line-subtle mt-150 border-t py-150 pt-350">
            <div className={`${NAV_EYEBROW} pb-150`}>{t("helpEyebrow")}</div>
            {helpItems.map((item) => {
              const inner = (
                <>
                  <span className="flex-1 text-left">{item.label}</span>
                  <span className="text-fg-subtle opacity-50">
                    <ChevronIcon size={14} />
                  </span>
                </>
              )
              return (
                <Button key={item.label} variant="quiet" fullWidth asChild onClick={onClose} className={NAV_ROW}>
                  {item.external ? (
                    <ExternalLink href={item.href} target="_blank" rel="noreferrer">
                      {inner}
                    </ExternalLink>
                  ) : (
                    <LocalizedClientLink href={item.href}>{inner}</LocalizedClientLink>
                  )}
                </Button>
              )
            })}
          </div>

          <div className="border-line-subtle mt-150 border-t px-400 pt-400 pb-150">
            <Stack gap="300" className="rounded-16x bg-brand-field text-fg-inverted shadow-raised relative overflow-hidden p-400">
              <div className="from-brand-sky/40 pointer-events-none absolute -top-10 -right-8 size-32 bg-radial to-transparent to-70%" aria-hidden="true" />
              <div className="from-brand/20 pointer-events-none absolute -bottom-12 -left-10 size-28 bg-radial to-transparent to-70%" aria-hidden="true" />
              <div className="relative">
                <Heading as="div" variant="sm" family="display" tone="inherit" className="font-bold">
                  {t("promoHeadline")}
                  <span className="block">{t("promoHeadlineAccent")}</span>
                </Heading>
                <Text as="p" variant="bodySm" family="text" tone="inverted" className="mt-100">
                  {t("promoSubtext")}
                </Text>
              </div>
              <Button variant="inverse" size="md" fullWidth asChild onClick={onClose} className="text-fg-brand relative font-bold">
                <ExternalLink href={signupHref}>{t("createAccount")}</ExternalLink>
              </Button>
              <Button
                variant="quiet"
                size="sm"
                fullWidth
                asChild
                onClick={onClose}
                className="text-fg-inverted hover:text-fg-inverted hover:bg-veil relative h-auto py-100 font-semibold"
              >
                <ExternalLink href={signupHref}>{t("signIn")}</ExternalLink>
              </Button>
            </Stack>
          </div>
          {HAS_LANGUAGE_CHOICE && (
            <div className="border-line-subtle mt-150 border-t px-400 py-350">
              <Row justify="between" align="center">
                <span className={`${NAV_LABEL} text-fg-strong`}>{tNav("language")}</span>
                <NavLanguageToggle />
              </Row>
            </div>
          )}
        </div>
        <div className="border-line-subtle pb-safe-offset-400 border-t px-400 pt-350">
          <StoreBadges size="sm" links={APP_STORE_LINKS} />
        </div>
      </Transition>
    </Portal>
  )
}
